import { usePanel } from '@hooks/usePanel';
import { cn } from '@utils/cn';
import { parseRoute } from '@utils/i18n';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router';
import { icons } from '../constants';
import { BOTTOM_NAV_CLASS } from './constants';
import { BottomNavMenuIcon } from './menuIcon';
import { getBottomNavRoutes, getRouteAliases, getRouteTab } from './helpers';
import { BottomNavProps } from './types';

const itemClasses = (active: boolean) =>
  cn(
    'relative flex h-14 w-full flex-col items-center justify-center gap-0.5 rounded-xl text-xs font-semibold text-neutral-500 active:bg-neutral-50 [&>svg]:-my-1 [&>svg]:h-9 [&>svg]:w-9 [&>svg]:shrink-0',
    active && 'bg-primary-50 text-primary-500 active:bg-primary-50',
  );

const ErrorDot = () => (
  <span className="absolute right-[calc(50%-20px)] top-1.5 h-2.5 w-2.5 rounded-full bg-danger-400" />
);

export const BottomNav = ({ routes, menuOpen, onMenu, tabError, menuError }: BottomNavProps) => {
  const { t } = useTranslation('app');
  const { pathname } = useLocation();
  const { parseUrl } = usePanel();
  const shortcuts = getBottomNavRoutes(routes);
  const isCurrent = (route: string) =>
    getRouteAliases(route).some((alias) => pathname.startsWith(`/${parseRoute(alias)}`));
  const moreActive = menuOpen || !shortcuts.some(isCurrent);

  return (
    <nav
      className={cn(
        BOTTOM_NAV_CLASS,
        'fixed inset-x-0 bottom-0 z-50 border-t border-neutral-100 bg-white px-2 pb-[env(safe-area-inset-bottom,0px)] pt-1 shadow-[0_-4px_16px_rgba(0,0,0,.06)] after:absolute after:inset-x-0 after:top-full after:h-40 after:bg-white',
      )}>
      <ul className="mx-auto grid max-w-xl grid-cols-4 gap-1 pb-1">
        {shortcuts.map((route) => {
          const tab = getRouteTab(route);
          return (
            <li key={route}>
              <NavLink
                onClick={(e) => {
                  if (pathname !== `/${parseRoute(route)}`) return;
                  e.preventDefault();
                }}
                to={`/${parseRoute(parseUrl(route))}`}
                className={() => itemClasses(!menuOpen && isCurrent(route))}>
                {icons[tab]}
                <span className="truncate">{t(`navigation.${tab}`)}</span>
                {tabError(tab) && <ErrorDot />}
              </NavLink>
            </li>
          );
        })}
        <li>
          <button
            type="button"
            aria-expanded={menuOpen}
            onClick={onMenu}
            className={itemClasses(moreActive)}>
            <BottomNavMenuIcon />
            <span>{t('navigation.menu')}</span>
            {menuError && <ErrorDot />}
          </button>
        </li>
      </ul>
    </nav>
  );
};
